import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, Shield, Sparkles, Power } from "lucide-react";
import { toast } from "react-toastify";

const ActionButtons = ({
  promoter,
  validationStatus,
  onApprove,
  onReject,
  onActivate,
  isLoading,
  canApprove,
  canApproveLimited,
}) => {
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decisionType, setDecisionType] = useState(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDecision = async () => {
    if (decisionType === "reject" && !decisionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    if (decisionType === "limited" && !decisionReason.trim()) {
      toast.error("Please provide a reason for limited access");
      return;
    }
    setIsSubmitting(true);
    
    try {
      if (decisionType === "reject") {
        await onReject(decisionReason);
      } else if (decisionType === "limited") {
        await onApprove("limited", decisionReason);
      } else if (decisionType === "full") {
        await onApprove("full", decisionReason);
      } else if (decisionType === "activate") {
        await onActivate();
      }
    } finally {
      setIsSubmitting(false);
      setShowDecisionModal(false);
      setDecisionType(null);
      setDecisionReason("");
    }
  };

  const openDecisionModal = (type) => {
    setDecisionType(type);
    setShowDecisionModal(true);
  };

  // Check if promoter is approved - can toggle activation status
  const isApproved = promoter?.affiliate_status === "full_approval" || promoter?.affiliate_status === "limited_approval";
  const isActive = promoter?.is_active === 1 || promoter?.is_active === true;
  const showToggleButton = isApproved;

  return (
    <>
      <div className="fixed right-0 bottom-0 left-0 z-40 p-6 bg-white border-t border-gray-200 shadow-lg">
        <div className="flex flex-col gap-4 mx-auto max-w-7xl sm:flex-row sm:justify-between sm:items-center">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              {validationStatus?.basicValidationPassed ? (
                <div className="flex gap-2 items-center text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Basic validation passed</span>
                </div>
              ) : (
                <div className="flex gap-2 items-center text-red-600">
                  <XCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Basic validation failed</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-600">
              {validationStatus?.basicValidationPassed
                ? "Proceed with quality assessment and risk review"
                : "Automatic rejection recommended"}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => openDecisionModal("reject")}
              disabled={isLoading}
              className="flex gap-2 items-center px-5 py-3 font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading && decisionType === "reject" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  <span className="hidden sm:inline">Reject</span>
                </>
              )}
            </button>
            <button
              onClick={() => openDecisionModal("limited")}
              disabled={isLoading || !canApproveLimited}
              className="flex gap-2 items-center px-5 py-3 font-medium text-white bg-yellow-600 rounded-lg transition-colors hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Approve with limited access and close monitoring"
            >
              {isLoading && decisionType === "limited" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span className="hidden sm:inline">Approve - Limited</span>
                </>
              )}
            </button>
            <button
              onClick={() => openDecisionModal("full")}
              disabled={isLoading || !canApprove}
              className="flex gap-2 items-center px-5 py-3 font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Approve with full access"
            >
              {isLoading && decisionType === "full" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="hidden sm:inline">Approve - Full</span>
                </>
              )}
            </button>

            {/* Toggle Activation Button - Show for approved promoters */}
            {showToggleButton && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => openDecisionModal("activate")}
                disabled={isLoading}
                className={`flex gap-2 items-center px-5 py-3 font-medium text-white rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                  isActive
                    ? "bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700"
                    : "bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700"
                }`}
                title={isActive ? "Deactivate publisher to disable promotions" : "Activate publisher to enable promotions and commissions"}
              >
                {isLoading && decisionType === "activate" ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Power className="w-5 h-5" />
                    <span className="hidden sm:inline">
                      {isActive ? "Deactivate Publisher" : "Activate Publisher"}
                    </span>
                    <span className="sm:hidden">
                      {isActive ? "Deactivate" : "Activate"}
                    </span>
                  </>
                )}
              </motion.button>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showDecisionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-60 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget && !isSubmitting) {
                setShowDecisionModal(false);
                setDecisionType(null);
                setDecisionReason("");
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl"
            >
              <div className="flex gap-3 items-center mb-4">
                {decisionType === "reject" && (
                  <>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: "spring" }}
                      className="p-2.5 bg-red-100 rounded-xl"
                    >
                      <XCircle className="w-6 h-6 text-red-600" />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Reject Promoter</h3>
                      <p className="text-xs text-gray-500">This action cannot be undone</p>
                    </div>
                  </>
                )}
                {decisionType === "limited" && (
                  <>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: "spring" }}
                      className="p-2.5 bg-yellow-100 rounded-xl"
                    >
                      <Shield className="w-6 h-6 text-yellow-600" />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Approve - Limited Access</h3>
                      <p className="text-xs text-gray-500">Restricted campaigns & monitoring</p>
                    </div>
                  </>
                )}
                {decisionType === "full" && (
                  <>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: "spring" }}
                      className="p-2.5 bg-green-100 rounded-xl"
                    >
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Approve - Full Access</h3>
                      <p className="text-xs text-gray-500">Standard campaign access</p>
                    </div>
                  </>
                )}
                {decisionType === "activate" && (
                  <>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: "spring" }}
                      className={`p-2.5 rounded-xl ${
                        isActive
                          ? "bg-gradient-to-br from-red-100 to-rose-100"
                          : "bg-gradient-to-br from-emerald-100 to-green-100"
                      }`}
                    >
                      <Power className={`w-6 h-6 ${isActive ? "text-red-600" : "text-emerald-600"}`} />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {isActive ? "Deactivate Publisher" : "Activate Publisher"}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {isActive ? "Disable promotions & commissions" : "Enable promotions & commissions"}
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-4 text-sm text-gray-600"
              >
                {decisionType === "reject" && "Please provide a detailed reason for rejecting this promoter application. This will be recorded for audit purposes."}
                {decisionType === "limited" && "This promoter will be approved with restricted access to specific campaigns and close monitoring. Provide a reason for limited access."}
                {decisionType === "full" && "This promoter will be approved with full access to standard campaigns. Optionally add notes or comments."}
                {decisionType === "activate" && (
                  isActive
                    ? "Deactivating this publisher will disable their ability to promote campaigns and earn commissions. They will lose access to live campaigns until reactivated."
                    : "Activating this publisher will enable them to start promoting live campaigns and earning commissions. They will gain full access to the platform based on their approval level."
                )}
              </motion.p>
              
              {decisionType === "activate" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className={`mb-4 p-4 rounded-lg border ${
                    isActive
                      ? "bg-red-50 border-red-200"
                      : "bg-emerald-50 border-emerald-200"
                  }`}
                >
                  <p className={`text-sm font-semibold mb-2 ${
                    isActive ? "text-red-900" : "text-emerald-900"
                  }`}>
                    {isActive ? "After Deactivation:" : "After Activation:"}
                  </p>
                  <ul className={`space-y-1 text-sm ${
                    isActive ? "text-red-800" : "text-emerald-800"
                  }`}>
                    {isActive ? (
                      <>
                        <li>• Publisher cannot promote live campaigns</li>
                        <li>• Not eligible to earn commissions</li>
                        <li>• Access to dashboard will be restricted</li>
                        <li>• Can be reactivated at any time</li>
                      </>
                    ) : (
                      <>
                        <li>• Publisher can promote live campaigns</li>
                        <li>• Eligible to earn commissions</li>
                        <li>• Access to performance dashboard</li>
                        <li>• Can track earnings and analytics</li>
                      </>
                    )}
                  </ul>
                </motion.div>
              )}
              
              <motion.textarea
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                placeholder={
                  decisionType === "reject"
                    ? "Enter detailed rejection reason (required)..."
                    : decisionType === "limited"
                    ? "Enter reason for limited access (required)..."
                    : decisionType === "activate"
                    ? isActive
                      ? "Optional: Add deactivation reason or notes..."
                      : "Optional: Add activation notes or comments..."
                    : "Add optional notes or comments..."
                }
                disabled={isSubmitting}
                className="p-3 mb-4 w-full text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                rows="4"
              />
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!isSubmitting) {
                      setShowDecisionModal(false);
                      setDecisionType(null);
                      setDecisionReason("");
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl transition-all hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDecision}
                  disabled={(decisionType !== "full" && decisionType !== "activate" && !decisionReason.trim()) || isLoading || isSubmitting}
                  className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                    decisionType === "reject"
                      ? "bg-red-600 hover:bg-red-700 active:bg-red-800"
                      : decisionType === "limited"
                      ? "bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800"
                      : decisionType === "activate"
                      ? isActive
                        ? "bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700"
                        : "bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700"
                      : "bg-green-600 hover:bg-green-700 active:bg-green-800"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      {decisionType === "reject" && (
                        <>
                          <XCircle className="w-4 h-4" />
                          <span>Confirm Rejection</span>
                        </>
                      )}
                      {decisionType === "limited" && (
                        <>
                          <Shield className="w-4 h-4" />
                          <span>Approve Limited</span>
                        </>
                      )}
                      {decisionType === "full" && (
                        <>
                          <Sparkles className="w-4 h-4" />
                          <span>Approve Full</span>
                        </>
                      )}
                      {decisionType === "activate" && (
                        <>
                          <Power className="w-4 h-4" />
                          <span>{isActive ? "Deactivate Publisher" : "Activate Publisher"}</span>
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ActionButtons;

